import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('website_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9pbtfiftmtnfcfh0k60', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后再试';
        setFormError(serverMsg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <ScrollToTop />
      <SchemaOrg
        type="WebPage"
        data={{
          name: '关于我们 - 蜜桃少年漫画',
          description: '了解蜜桃少年漫画的品牌故事、口碑评价与联系方式。178漫画网旗下专业少年漫画阅读平台。',
          url: 'https://sdidsa.cn/about',
          isPartOf: { '@type': 'WebSite', name: '蜜桃少年漫画', url: 'https://sdidsa.cn' },
        }}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'https://sdidsa.cn' },
            { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://sdidsa.cn/about' },
          ],
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="bg-background-100">
            <div className="w-full px-4 md:px-6 py-8">
              <Breadcrumb
                items={[
                  { label: '首页', to: '/' },
                  { label: '关于我们' },
                ]}
              />
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mt-2">
                关于我们
              </h1>
            </div>
          </div>

          <section className="py-12 md:py-16">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-4xl mx-auto">
                <div className="mb-14">
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-5">
                    品牌介绍
                  </h2>
                  <div className="bg-background-50 rounded-2xl border border-background-200/70 p-6 md:p-8 space-y-4">
                    <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                      <strong>蜜桃少年漫画</strong>是<strong>178漫画网</strong>旗下的专业少年向漫画阅读平台，致力于为广大漫画爱好者提供最优质的在线阅读体验。我们专注于热血、冒险、搞笑、奇幻、恋爱、悬疑、科幻、校园等少年向漫画类型，平台所有漫画均为正版授权或获得合法授权转载，确保内容的合规性与高质量。
                    </p>
                    <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                      自成立以来，蜜桃少年漫画始终坚持<strong>"免费阅读、无删减、高清画质"</strong>的服务理念。我们深知每一位漫画读者对于作品完整性和画面质量的高要求，因此在内容呈现上严格把关，确保每一部漫画都能以最完美的状态呈现在读者面前。
                    </p>
                    <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                      平台采用先进的<strong>下拉式阅读技术</strong>，读者无需翻页，只需向下滑动即可流畅阅读，在手机端的体验尤其出色。同时，我们不断优化网站性能，确保页面加载速度快、阅读过程无卡顿，让您随时随地沉浸在漫画的世界中。
                    </p>
                    <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                      目前，蜜桃少年漫画已收录<strong>超过500部</strong>优质漫画作品，涵盖八大热门分类，每日保持高频更新。我们与多家知名漫画工作室和独立漫画家建立了长期合作关系，持续引入新鲜优质内容，丰富平台的作品库。
                    </p>
                  </div>
                </div>

                <div className="mb-14">
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-5">
                    口碑评价
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    {[
                      {
                        name: '漫画爱好者小明',
                        text: '用了蜜桃少年漫画快一年了，画质真的没得说，更新也很快。最爱的就是下拉式阅读方式，躺在床上看简直太舒服了！',
                        rating: 5,
                      },
                      {
                        name: '资深读者老王',
                        text: '对比了好几个漫画平台，蜜桃少年漫画的综合体验是最好的。无删减这一点太重要了，很多平台都会删减内容，这里完全不用担心。',
                        rating: 5,
                      },
                      {
                        name: '学生党小李',
                        text: '全部免费真的太良心了！作为一个学生党，不用花钱就能看到这么多高清漫画，178漫画网真的是我们的福音。',
                        rating: 5,
                      },
                    ].map((review, idx) => (
                      <div key={idx} className="bg-background-50 rounded-xl border border-background-200/70 p-5">
                        <div className="flex items-center gap-1 mb-3">
                          {Array.from({ length: review.rating }).map((_, i) => (
                            <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                          ))}
                        </div>
                        <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                          &ldquo;{review.text}&rdquo;
                        </p>
                        <p className="text-xs text-foreground-500 font-medium">
                          — {review.name}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-14">
                  <div className="text-center p-6 bg-background-50 rounded-xl border border-background-200/70">
                    <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-2xl bg-primary-100 text-primary-600 mb-4">
                      <i className="ri-book-read-line text-2xl"></i>
                    </div>
                    <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">500+</h3>
                    <p className="text-sm text-foreground-500">优质漫画作品</p>
                  </div>
                  <div className="text-center p-6 bg-background-50 rounded-xl border border-background-200/70">
                    <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-2xl bg-accent-100 text-accent-600 mb-4">
                      <i className="ri-user-heart-line text-2xl"></i>
                    </div>
                    <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">1000万+</h3>
                    <p className="text-sm text-foreground-500">月活跃读者</p>
                  </div>
                  <div className="text-center p-6 bg-background-50 rounded-xl border border-background-200/70">
                    <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-2xl bg-secondary-100 text-secondary-600 mb-4">
                      <i className="ri-thumb-up-line text-2xl"></i>
                    </div>
                    <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">99.2%</h3>
                    <p className="text-sm text-foreground-500">读者满意度</p>
                  </div>
                </div>

                <div>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-5">
                    联系我们
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="bg-background-50 rounded-xl border border-background-200/70 p-6">
                      <h3 className="font-heading text-base font-semibold text-foreground-900 mb-4">联系方式</h3>
                      <div className="space-y-3">
                        <div className="flex items-center gap-3 text-sm text-foreground-600">
                          <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                            <i className="ri-mail-line"></i>
                          </div>
                          <span>contact@sdidsa.cn</span>
                        </div>
                        <div className="flex items-center gap-3 text-sm text-foreground-600">
                          <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                            <i className="ri-customer-service-line"></i>
                          </div>
                          <span>在线客服：工作日 9:00 - 18:00</span>
                        </div>
                        <div className="flex items-center gap-3 text-sm text-foreground-600">
                          <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 flex-shrink-0">
                            <i className="ri-map-pin-line"></i>
                          </div>
                          <span>中国·北京市朝阳区</span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-background-50 rounded-xl border border-background-200/70 p-6">
                      <h3 className="font-heading text-base font-semibold text-foreground-900 mb-4">发送消息</h3>
                      <form data-readdy-form="true" onSubmit={handleSubmit} className="space-y-3">
                        <div>
                          <input
                            type="text"
                            name="name"
                            placeholder="您的姓名"
                            required
                            className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                          />
                        </div>
                        <div>
                          <input
                            type="email"
                            name="email"
                            placeholder="您的邮箱"
                            required
                            className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                          />
                        </div>
                        <div>
                          <textarea
                            name="message"
                            placeholder="请输入您的留言内容（最多500字）"
                            rows={3}
                            maxLength={500}
                            required
                            className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                          ></textarea>
                        </div>
                        <div className="spam-field">
                          <input
                            type="text"
                            name="website_alt"
                            tabIndex={-1}
                            autoComplete="off"
                            aria-hidden="true"
                            readOnly
                          />
                        </div>
                        <button
                          type="submit"
                          className="w-full px-6 py-2.5 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          发送消息
                        </button>

                        {formStatus === 'success' && (
                          <p className="text-sm text-green-600 text-center">消息已发送成功，我们会尽快回复您！</p>
                        )}
                        {formStatus === 'error' && (
                          <p className="text-sm text-red-600 text-center">{formError}</p>
                        )}
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}